import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { CaretLeft, UserSwitch, Buildings, UploadSimple, Trash, EnvelopeSimple, WhatsappLogo, Copy, Check, X, ArrowsClockwise, PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";
import ImportPlayersModal from "@/components/ImportPlayersModal";
import ConfirmDeleteClubModal from "@/components/ConfirmDeleteClubModal";

export default function SuperClubDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hcInvite, setHcInvite] = useState(null); // pending head-coach invite (or null)
  const [hcModalOpen, setHcModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const refresh = () =>
    api.get(`/super/clubs/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));

  const refreshHcInvite = () =>
    api.get(`/super/clubs/${id}/head-coach-invite`)
      .then((r) => setHcInvite(r.data || null))
      .catch(() => setHcInvite(null));

  useEffect(() => { refresh(); refreshHcInvite(); }, [id]);

  const impersonate = async (userId) => {
    try {
      const orig = localStorage.getItem("tcd_token");
      const res = await api.post(`/super/impersonate/${userId}`);
      if (res.data?.access_token) {
        if (orig) localStorage.setItem("tcd_token_orig", orig);
        localStorage.setItem("tcd_token", res.data.access_token);
      }
      const me = await api.get("/auth/me");
      setUser(me.data);
      // Replace history entry so browser-back doesn't return here as impersonated user
      nav("/", { replace: true });
    } catch (e) {
      alert("Could not impersonate this user.");
    }
  };

  const cancelHcInvite = async () => {
    if (!hcInvite) return;
    await api.delete(`/invites/${hcInvite.id}`);
    setHcInvite(null);
  };

  if (!data) return <div className="font-label">Loading…</div>;
  if (data.error) return <div className="surface p-8 text-zinc-400">Club not found or you don&apos;t have access.</div>;
  const { club, users, players, recent_audit } = data;
  const headCoach = users.find((u) => u.role === "head_coach");
  const coaches = users.filter((u) => u.role === "coach");
  const parents = users.filter((u) => u.role === "parent");

  return (
    <div className="space-y-5" data-testid="super-club-detail">
      <Link to="/super/clubs" className="btn-ghost inline-flex items-center gap-1.5 -ml-2"><CaretLeft size={18} weight="bold" /> Back to clubs</Link>

      <div className="surface p-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-md bg-[#1a1a1a] border border-[#27272a] flex items-center justify-center">
            <Buildings size={24} className="text-[#dfff00]" weight="duotone" />
          </div>
          <div>
            <div className="font-label">Club</div>
            <h1 className="font-heading uppercase text-3xl tracking-tight">{club.name}</h1>
            <div className="text-xs text-zinc-500 mt-1">{club.country} • {club.timezone} • {club.subscription_status}</div>
          </div>
        </div>
        {headCoach && (
          <button onClick={() => impersonate(headCoach.id)} className="btn-secondary" data-testid="impersonate-head">
            <UserSwitch size={16} weight="bold" /> Login as admin
          </button>
        )}
      </div>

      {/* No head coach yet — surface an invite card */}
      {!headCoach && (
        <div className="surface p-5 border border-[#dfff00]/40" data-testid="no-head-coach-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-label text-[#dfff00]">No admin yet</div>
              <h2 className="font-heading uppercase text-xl mt-1">Invite the club admin</h2>
              <p className="text-zinc-400 text-sm mt-2 max-w-lg">
                Optionally import players first, then send the club admin an activation
                link via WhatsApp or email. They&apos;ll pick their own password.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {hcInvite ? (
                <>
                  <span className="text-xs px-2 py-1 rounded uppercase tracking-wider font-bold bg-[#dfff00]/10 text-[#dfff00] self-center" data-testid="hc-invite-status">
                    invite pending
                  </span>
                  <button onClick={() => setShareOpen(true)} className="btn-primary" data-testid="hc-invite-share">
                    <PaperPlaneTilt size={14} weight="bold" /> Share link
                  </button>
                  <button onClick={cancelHcInvite} className="btn-ghost text-[#ff0026]" data-testid="hc-invite-cancel">
                    <Trash size={14} weight="bold" />
                  </button>
                </>
              ) : (
                <button onClick={() => setHcModalOpen(true)} className="btn-primary" data-testid="hc-invite-btn">
                  <PaperPlaneTilt size={14} weight="bold" /> Invite club admin
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setImportOpen(true)} className="btn-primary" data-testid="import-players-btn">
          <UploadSimple size={14} weight="bold" /> Import players (.xlsx)
        </button>
        <button onClick={() => setHcModalOpen(true)} className="btn-secondary" data-testid="invite-admin-btn">
          <PaperPlaneTilt size={14} weight="bold" /> Invite club admin
        </button>
        <button
          onClick={() => setDeleteOpen(true)}
          className="btn-ghost text-[#ff0026] hover:bg-[#ff0026]/10 ml-auto"
          data-testid="delete-club-btn"
        >
          <Trash size={14} weight="bold" /> Delete this club
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Players" value={players.length} />
        <Stat label="Coaches" value={coaches.length + (headCoach ? 1 : 0)} />
        <Stat label="Parents" value={parents.length} />
      </div>

      <section>
        <div className="font-label mb-2">Users</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {users.map((u) => {
            const editable = u.role === "coach" || u.role === "head_coach";
            const isInactive = u.active === false;
            return (
              <div key={u.id} className={`surface p-3 flex items-center justify-between gap-2 ${isInactive ? "opacity-60" : ""}`} data-testid={`club-user-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2">
                    {u.name}
                    {isInactive && <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">disabled</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="role-badge">{roleLabel(u)}</span>
                  {editable && (
                    <button onClick={() => setEditingUser(u)} className="btn-ghost text-xs" data-testid={`edit-user-${u.id}`} title="Edit user">
                      <PencilSimple size={14} weight="bold" />
                    </button>
                  )}
                  {u.role !== "super_admin" && (
                    <button onClick={() => impersonate(u.id)} className="btn-ghost text-xs" data-testid={`impersonate-${u.id}`} title="Login as this user">
                      <UserSwitch size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div className="font-label mb-2">Players</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {players.map((p) => (
            <div key={p.id} className="surface p-3" data-testid={`club-player-${p.id}`}>
              <div className="font-semibold text-sm">{p.name}</div>
              <div className="text-xs text-zinc-500">DOB: {p.date_of_birth}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="font-label mb-2">Recent activity</div>
        <div className="surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#1a1a1a] text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-label">When</th>
                <th className="text-left px-3 py-2 font-label">Action</th>
                <th className="text-left px-3 py-2 font-label">Entity</th>
              </tr>
            </thead>
            <tbody>
              {recent_audit.map((l) => (
                <tr key={l.id} className="border-t border-[#27272a]">
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2"><span className="font-semibold text-[#dfff00]">{l.action}</span></td>
                  <td className="px-3 py-2 text-zinc-300">{l.entity_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ImportPlayersModal
        clubId={id}
        clubName={club.name}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => refresh()}
      />
      <ConfirmDeleteClubModal
        open={deleteOpen}
        club={{ id, name: club.name }}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => nav("/super/clubs", { replace: true })}
      />
      <HeadCoachInviteModal
        open={hcModalOpen}
        clubId={id}
        onClose={() => setHcModalOpen(false)}
        onCreated={(inv) => {
          setHcInvite(inv);
          setHcModalOpen(false);
          setShareOpen(true);
        }}
      />
      {hcInvite && shareOpen && (
        <HeadCoachShareModal
          invite={hcInvite}
          clubName={club.name}
          onClose={() => setShareOpen(false)}
          onRegenerated={(newToken) => setHcInvite({ ...hcInvite, token: newToken })}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    title: user.title || "",
    role: user.role,
    active: user.active !== false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      // Only send fields that changed
      const payload = {};
      if (form.name !== user.name) payload.name = form.name;
      if (form.email.toLowerCase() !== (user.email || "").toLowerCase()) payload.email = form.email;
      if ((form.phone || "") !== (user.phone || "")) payload.phone = form.phone;
      if ((form.title || "") !== (user.title || "")) payload.title = form.title;
      if (form.role !== user.role) payload.role = form.role;
      if (form.active !== (user.active !== false)) payload.active = form.active;
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await api.patch(`/super/users/${user.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label">Edit user</div>
            <h2 className="font-heading uppercase text-xl">{user.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="font-label block mb-1">Name</label>
            <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" />
          </div>
          <div>
            <label className="font-label block mb-1">Email</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} data-testid="edit-user-email" />
          </div>
          <div>
            <label className="font-label block mb-1">Phone</label>
            <input className="input" placeholder="+447700900111" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-user-phone" />
          </div>
          <div>
            <label className="font-label block mb-1">Title (optional)</label>
            <input className="input" placeholder="e.g. Director of Tennis" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-user-title" />
            <div className="text-[10px] text-zinc-500 mt-1">Shown instead of the role label. Access is still controlled by the role below.</div>
          </div>
          <div>
            <label className="font-label block mb-1">Role</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="edit-user-role">
              <option value="coach">coach</option>
              <option value="head_coach">admin (full club access)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="edit-user-active" />
            <span className="text-sm">Active (uncheck to disable login)</span>
          </label>
          {err && <div className="text-sm text-[#ff0026]">{err}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="edit-user-submit">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function HeadCoachInviteModal({ open, clubId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", title: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.post(`/super/clubs/${clubId}/invite-head-coach`, form);
      onCreated(res.data);
      setForm({ name: "", email: "", phone: "", title: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="hc-invite-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading uppercase text-xl">Invite club admin</h2>
          <button type="button" onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="font-label block mb-1">Name</label>
            <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hc-form-name" />
          </div>
          <div>
            <label className="font-label block mb-1">Email</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} data-testid="hc-form-email" />
          </div>
          <div>
            <label className="font-label block mb-1">Phone (optional, for WhatsApp)</label>
            <input className="input" placeholder="+447700900111" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="hc-form-phone" />
          </div>
          <div>
            <label className="font-label block mb-1">Title (optional)</label>
            <input className="input" placeholder="e.g. Director of Tennis, Academy Manager" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hc-form-title" />
            <div className="text-[10px] text-zinc-500 mt-1">Defaults to “admin”. Either way they get full club-admin access.</div>
          </div>
          {err && <div className="text-sm text-[#ff0026]">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="hc-form-submit">
            {busy ? "Creating…" : "Create invite link"}
          </button>
        </div>
      </form>
    </div>
  );
}

function normalisePhone(raw) {
  if (!raw) return "";
  let s = String(raw).trim().replace(/[^\d+]/g, "");
  if (s.startsWith("+")) return s.slice(1);
  if (s.startsWith("00")) return s.slice(2);
  if (s.startsWith("0")) return "44" + s.slice(1);
  return s;
}

function HeadCoachShareModal({ invite, clubName, onClose, onRegenerated }) {
  const [copied, setCopied] = useState(false);
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [emailErr, setEmailErr] = useState("");

  const sendEmail = async () => {
    if (!invite.parent_email || emailState === "sending") return;
    setEmailState("sending");
    setEmailErr("");
    try {
      await api.post(`/invites/${invite.id}/send-email`, { base_url: window.location.origin });
      setEmailState("sent");
    } catch (e) {
      setEmailState("error");
      setEmailErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const url = `${window.location.origin}/invite/${invite.token}`;
  const message =
    `Hi ${invite.parent_name}, you've been invited to lead ${clubName} on courtsideOS. ` +
    `Activate your head-coach account here: ${url}`;
  const phone = normalisePhone(invite.parent_phone);
  const waUrl = phone
    ? `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }
  };

  const regenerate = async () => {
    const res = await api.post(`/invites/${invite.id}/resend`);
    if (res.data?.token) onRegenerated(res.data.token);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="hc-share-modal">
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label">Head-coach invite ready</div>
            <h2 className="font-heading uppercase text-xl">Share with {invite.parent_name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        <div className="bg-[#09090b] border border-[#27272a] rounded-md p-3 mb-4">
          <div className="font-label mb-1">Activation link</div>
          <div className="text-xs text-zinc-300 break-all font-mono" data-testid="hc-share-link">{url}</div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="surface p-4 flex flex-col items-center gap-2 hover:border-[#16ec06]/60 transition-colors text-[#16ec06]"
            data-testid="hc-share-whatsapp"
            title={phone ? `Will message +${phone}` : "Will open WhatsApp share picker"}
          >
            <WhatsappLogo size={28} weight="fill" />
            <span className="text-xs font-bold uppercase tracking-wider">WhatsApp</span>
            <span className="text-[10px] text-zinc-500 font-mono">{phone ? `+${phone}` : "share"}</span>
          </a>
          <button
            type="button"
            onClick={sendEmail}
            disabled={!invite.parent_email || emailState === "sending"}
            className={`surface p-4 flex flex-col items-center gap-2 transition-colors ${invite.parent_email ? "hover:border-[#dfff00]/60 text-[#dfff00]" : "opacity-40 cursor-not-allowed text-zinc-500"}`}
            data-testid="hc-share-email"
            title={invite.parent_email ? `Send to ${invite.parent_email}` : "No email on this invite"}
          >
            {emailState === "sent" ? <Check size={28} weight="bold" className="text-[#16ec06]" /> : <EnvelopeSimple size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">
              {emailState === "sending" ? "Sending…" : emailState === "sent" ? "Sent" : "Email"}
            </span>
          </button>
          <button
            onClick={copy}
            className="surface p-4 flex flex-col items-center gap-2 hover:border-[#e3735e]/60 transition-colors text-[#e3735e]"
            data-testid="hc-share-copy"
          >
            {copied ? <Check size={28} weight="bold" /> : <Copy size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>

        {emailState === "error" && (
          <div className="text-xs text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-2 mt-3" data-testid="hc-share-email-error">
            {emailErr}
          </div>
        )}

        <button onClick={regenerate} className="btn-ghost w-full mt-4 text-xs" data-testid="hc-share-regenerate">
          <ArrowsClockwise size={14} weight="bold" /> Regenerate link
        </button>

        <p className="text-xs text-zinc-500 mt-4">
          They click the link, set a password, and are auto-signed-in as an admin of {clubName}.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface p-4">
      <div className="font-label">{label}</div>
      <div className="font-heading text-4xl text-[#dfff00] mt-1">{value}</div>
    </div>
  );
}
